import {DataGrid} from "@mui/x-data-grid";
import {Box, Tooltip, Typography} from "@mui/material";
import {useEffect, useState} from "react";
import CustomAlert from "../components/CustomAlert";
import {CAP_LEVELS} from "../domain/CapLevels";
import {useMutation, useQuery} from "@apollo/client";
import {
    ALL_PERFORMERS_CONTENT_AND_ACCREDITATION,
    CREATE_CONTENT_PERFORMING_ACCREDITATION,
    DELETE_CONTENT_PERFORMING_ACCREDITATION
} from "../queries/GraphQLQueries";
import Loader from "../components/Loader";


const DEFAULT_COLUMNS = [
    {field: 'displayName', headerName: 'Name',  width: 100, renderHeader: (params) => {
            return <Tooltip title={params.colDef.headerName}>
                <Typography variant="body2">{params.colDef.headerName}</Typography>
            </Tooltip>
        }, }
];

export default function PerformingAccreditationView(props) {
    const {data, loading, error} = useQuery(ALL_PERFORMERS_CONTENT_AND_ACCREDITATION);
    const [synthesizedColumns, setSynthesizedColumns] = useState([]);
    const [sythesizedRows, setSynthesizedRows] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [createAccreditation, {updatedData, loadingMutate: loadingCreateAccreditation, mutateError}] = useMutation(CREATE_CONTENT_PERFORMING_ACCREDITATION, {
        refetchQueries: [{query: ALL_PERFORMERS_CONTENT_AND_ACCREDITATION}, 'GetAllPerformersContentAndAccreditation']});
    const [deleteAccreditation, {}] = useMutation(DELETE_CONTENT_PERFORMING_ACCREDITATION);


    useEffect(() => {
        if(!data || loading) {
            return;
        }

        // let's synthesize some column headers!
        let newColumnHeaders = data.contentInfos.map((contentInfo) => {
            return {
                field: contentInfo.id.toString(),
                headerName: contentInfo.name,
                type: 'boolean',
                width: 140,
                editable: true,
                renderHeader: columnRenderer,
                capLevel: contentInfo.level
            }
        });
        setSynthesizedColumns([...DEFAULT_COLUMNS, ...newColumnHeaders]);

        let newSythesizedRows = data.users_filtered.map((user) => {
            let rowObject = {
                id: user.id,
                displayName: user.displayName
            }

            data.contentInfos.map((contentInfo) => {
                let foundAccreditation = data.contentPerformingAccreditations.find(accreditation => {
                        return accreditation.contentInfo.id === contentInfo.id && accreditation.domainUser.id === user.id});
                rowObject[contentInfo.id] = foundAccreditation != null;
            });

            return rowObject;
        });

        setSynthesizedRows(newSythesizedRows);

    }, [data, loading]);

    function columnRenderer(params) {
        const capLevelInfo = CAP_LEVELS.find(level => level.key === params.colDef.capLevel);

        return (
            <Tooltip title={params.colDef.headerName}>
                <Box className="text-ellipsis"
                     style={{backgroundColor: capLevelInfo.colorSubtle, padding: '1.2rem'}}>
                    {params.colDef.headerName}
                </Box>
            </Tooltip>
        );
    }

    function processRowUpdate(newRow, oldRow) {
        const changedContentId = Object.keys(newRow).find((newRowKey) => {
            return newRow[newRowKey] !== oldRow[newRowKey];
        });

        if (!changedContentId) {
            // nothing changed
            return new Promise(function(resolve) {
              resolve(newRow);
            });
        }

        const isAccredited = newRow[changedContentId];
        const userId = newRow.id;

        let mutationExecutionOptions = {
            variables: {
                contentInfoId: changedContentId,
                domainUserId: userId
            }
        };

        if (isAccredited) {
            setAlertMessage({children: 'Saving Accreditation...', severity: 'success'});
        } else {
            setAlertMessage({children: 'Deleting Accreditation...', severity: 'warning'});
        }

        return new Promise(function(resolve, reject) {
                if (isAccredited) {
                    createAccreditation(mutationExecutionOptions)
                        .then(() => { resolve(newRow) })
                        .catch((e) => { reject() });
                } else {
                    const accreditationRecordId = data.contentPerformingAccreditations
                        .find(function (accreditation) {
                            return accreditation.contentInfo.id == changedContentId && accreditation.domainUser.id === userId;
                        })
                        ?.id;

                    mutationExecutionOptions = {
                        ...mutationExecutionOptions,
                        update(cache) {
                            const normalizedId = cache.identify({accreditationRecordId, __typename: "ContentPerformingAccreditation"})
                            cache.evict({id: normalizedId});
                            cache.gc();
                        }};

                    deleteAccreditation(mutationExecutionOptions)
                        .then(() => { resolve(newRow) })
                        .catch((e) => { reject() });
                }
        });
    }

    function handleClose() {
        setAlertMessage(null);
    }

    if (loading || loadingCreateAccreditation) {
        return <Loader/>;
    }

    return ( <div style={{ width: '100%' }}>           
        <DataGrid
            className={'mui-table'}
            autoHeight={true}
            columns={synthesizedColumns}
            rows={data && sythesizedRows}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={e => {
                console.error("error processing row " + e);
            }}
            experimentalFeatures={{newEditingApi: true}}
        />
        <CustomAlert alertMessage={alertMessage}  handleCloseCustomAlert={handleClose}/>
    </div>);
}
